<template>
  <div data-scroll-el="productList" />
  <product-list-header
    v-slot="attrs"
    v-model:filters-shown="filtersShown"
    :currency
    :filters
    :filters-query="filtersQuery"
    :list
    :next-page-loading="nextPageLoading"
    :overlay-title="overlayTitle"
    :pickup-filters="pickupFilters"
    :products-loading="productsLoading"
    :side-filters="sideFilters"
    :sort-options="sortOptions"
    :sort-state="`${sortState}`"
    :store-filter-state="storeFilterState"
    :title="headerTitle"
    @change="selectAndFilterBy"
    @change-store="filterByStore"
    @clear="clearFilters"
    @remove="removeFilter"
    @sort="selectAndSortBy"
  >
    <dialog-filters
      v-bind="attrs"
      :currency
      :filters
      :filters-query="filtersQuery"
      :heading="headerTitle || $t.filterAndSort"
      :loading="productsLoading || nextPageLoading"
      :pickup-filters="pickupFilters"
      :sort-options="sortOptions"
      :sort-state="sortState"
      :store-filter-state="storeFilterState"
      :total
      @change="selectAndFilterBy"
      @change-store="filterByStore"
      @clear="clearFilters"
      @remove="removeFilter"
      @sort="selectAndSortBy"
      @update="selectAndFilterBy"
    />
  </product-list-header>
  <div :class="brandClasses.outerGridWrapper">
    <div
      v-if="gridSizeSelector && (list?.products.length || hasFilters)"
      :class="brandClasses.gridSizeSelector"
    >
      <base-button
        v-for="(count, label) in { [$t.defaultView]: '4', [$t.compactView]: '6' }"
        :key="count"
        :aria-pressed="colsLg === count"
        @click="colsLg = count"
      >
        <span class="sr-only">{{ label }}</span>
        <span
          class="block cursor-pointer b-b duration peer-focus-visible:outline-auto "
          :class="colsLg === count ? 'b-grey-10 c-grey-10' : 'b-transparent c-grey-40 hover:b-grey-40'"
        >
          <vf-icon :name="`square-${count}`" size="md" />
        </span>
      </base-button>
    </div>

    <slot name="compare" />

    <div :class="brandClasses.outerGrid">
      <base-sticky :margin="headerPLPHeight" name="plpSideFilters" position="top">
        <product-list-filters
          v-if="sideFilters"
          id="plp-filters"
          class="sticky h-screen shrink-0 self-start overflow-x-hidden overflow-y-auto pb-4 pr-2 scrollbar-sm duration <lg:hidden "
          :class="{ '': !filtersShown }"
          :currency
          :filters
          :filters-query="filtersQuery"
          :loading="productsLoading"
          :pickup-filters="pickupFilters"
          :side-filters="sideFilters"
          :sort-options="sortOptions"
          :sort-state="`${sortState}`"
          :store-filter-state="storeFilterState"
          :style="{
            top: pxToRem(headerPLPHeight),
            maxHeight: `calc(100vh - ${filtersTop})`,
          }"
          :total
          @change="selectAndFilterBy"
          @change-store="filterByStore"
          @clear="clearFilters"
          @remove="removeFilter"
          @sort="selectAndSortBy"
          @update="selectAndFilterBy"
        >
          <template #title>
            <div class="b-b b-grey-80 py-2 subtitle-3 ">
              {{ $t.filterAndSort }}
            </div>
          </template>
        </product-list-filters>
      </base-sticky>
      <div class="w-full" :class="[brandClasses.innerGridWrapper, { 'lg:col-span-3': sideFilters }]">
        <template v-if="pickupFilters && sideFilters && pickupFilterAboveList">
          <base-sticky v-slot="{ isStuck }" name="pickupFiltersPLP">
            <div
              class="sticky top-0 bg-white"
              :class="[
                { '<lg:hidden': sideFilters },
                isStuck ? 'z-50' : 'z-2',
              ]"
            >
              <filter-pickup :class="isStuck ? 'pt-4' : ''" :model-value="storeFilterState" @update:model-value="filterByStore($event)" />
            </div>
          </base-sticky>
        </template>
        <div v-if="$feature.enablePLPESpots && showAboveGridBelowHeadingEspot && (eSpotsPending || eSpot?.items?.length)" class="mb-4 md:hidden">
          <!-- Only show e-spot on initial load of PLP and not while PLP is being filtered -->
          <div v-if="eSpotsPending" v-style:h="{ sm: '8.5rem', md: '17.25rem', lg: '23.26rem' }" class="skeleton" />
          <cms-section v-else-if="eSpot" page-context :section="eSpot" />
        </div>
        <product-list-no-results v-if="!productsLoading && !products.length" :class="brandClasses.noResults" :pickup-filters="pickupFilters" />
        <!-- Product grid -->
        <base-grid v-else ref="gridRef" :cols :gap-x="grid.gaps.x" :gap-y="grid.gaps.y">
          <!-- hydration mode is set to visible because we need to calculate the number of hidden product variants -->
          <template v-if="!productsLoading">
            <template v-for="(product, i) in products" :key="product.id">
              <base-lazy-hydrate
                v-if="i < renderedProducts"
                :interaction="['mouseenter', 'focus', 'touchstart']"
                :when="hydrationMode"
              >
                <product-card
                  v-bind="{ breadcrumbs, hideRating, product, quickshopMode, storeFilterState, trackingCategory }"
                  :class="{ '<md:col-span-2': showFocusedProduct && product.focusedProduct }"
                  :image-fetch-priority="i < +colsLg ? 'high' : 'auto'"
                  :lazy="i >= +colsLg"
                  :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
                  :show-swatches="$feature.showSwatchesInProductList"
                  :tracking-offset="i"
                />
              </base-lazy-hydrate>

              <!-- Makes sure all product links are available after SSR and can be crawled -->
              <base-lazy-hydrate v-else when="visible">
                <base-link ref="lazyLoadRefs" :aria-label="product.name" :to="product.url">
                  <product-card-skeleton :show-swatches="$feature.showSwatchesInProductList" />
                </base-link>
              </base-lazy-hydrate>
            </template>

            <slot v-bind="{ cols }" />
          </template>
          <!-- Skeleton -->
          <template v-if="productsLoading || nextPageLoading">
            <product-card-skeleton
              v-for="i in (nextPageLoading ? nextPageSize : productsPerPage)"
              :key="`product-${i}`"
              :show-swatches="$feature.showSwatchesInProductList"
            />
          </template>
        </base-grid>

        <!-- Pagination Skeleton -->
        <product-list-pagination-skeleton v-if="productsLoading" />
        <!-- Pagination -->
        <product-list-pagination
          v-else-if="products.length"
          :class="brandClasses.pagination"
          :current="currentPage || products.length"
          :next-page="nextPage"
          :page-size="nextPageSize"
          :previous-page="previousPage"
          :total="totalPages || total"
          @load-more="loadMore"
          @next="$nextPage => emit('next', $nextPage)"
          @previous="$previousPage => emit('previous', $previousPage)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { BaseGrid as BaseGridType } from '#components'
import type { PlpAboveGridBelowHeadingEspotContext } from '#content/contexts/plp'
import type { Catalog, Search } from '#root/api/clients/product/data-contracts'
import type { QuickshopMode } from '#types/catalog'
import type { Responsive } from '#types/common'
import { PlpAboveGridBelowHeadingEspotContextKey } from '#content/contexts/plp'

export type Props = {
  currentPage?: number
  gridSizeSelector: boolean
  headerTitle?: string
  hideRating?: boolean
  list: Catalog | Search | null
  nextPage?: number
  nextPageLoading: boolean
  overlayTitle?: boolean
  pickupFilters: boolean
  previousPage?: number
  productsLoading: boolean
  quickshopMode: QuickshopMode
  sideFilters: boolean
  totalPages?: number
  trackingCategory?: string
}

export type Emits = {
  'load-more': []
  'next': [page: number]
  'previous': [page: number]
}

const { list, sideFilters } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineSlots<{
  default: (props: { cols: Responsive<number> }) => void
  compare: void
}>()

const {
  brandClasses,
  eagerLoadedCards,
  grid,
  hydrationMode,
  pickupFilterAboveList,
  productsPerPage,
  showAboveGridBelowHeadingEspot,
} = useAppConfig().components.product.list
const {
  clearFilters,
  filterBy,
  filterByStore,
  filters,
  filtersQuery,
  removeFilter,
  sortBy,
  sortOptions,
  sortState,
  storeFilterState
} = useFilters(
  () => list?.filters || [],
  () => scrollToElement('productList', { preventFocus: true })
)
const header = useHeaderStore()
const { $t, $feature } = useNuxtApp()
const route = useRoute()
const stickyState = useSticky()

const {
  eSpot,
  eSpotsPending
} = inject(PlpAboveGridBelowHeadingEspotContextKey, {} as PlpAboveGridBelowHeadingEspotContext)

const colsLg = ref(`${sideFilters ? grid.cols.lg - 1 : grid.cols.lg}`)
const filtersShown = ref(false)
const gridRef = ref<typeof BaseGridType>()
const lazyLoadRefs = ref<HTMLAnchorElement[]>([])
const productsToRender = ref(eagerLoadedCards)

const total = computed(() => list?.total || 0)
const currency = computed(() => list?.currency || 'USD')
const breadcrumbs = computed(() => list?.breadcrumbs || [])
const products = computed(() => list?.products || [])
const cols = computed(() => ({ ...grid.cols, lg: +colsLg.value }))
const nextPageSize = computed(() => Math.min(productsPerPage, total.value - products.value.length))
const hasFilters = computed(() => !!Object.keys(filters.value)?.length)
const renderedProducts = computed(() => Math.min(productsToRender.value, products.value.length))
const showFocusedProduct = computed(
  () => $feature.showFocusedProductOnRoutes.some((pattern) => convertPatternToRegex(pattern).test(route.fullPath))
)

const { height: headerPLP } = useElementBounding(() => stickyState.value.headerPLP)
const headerPLPHeight = computed(() => headerPLP.value + (header.pageScrolledUp ? header.height.header : 0))
const { top: gridTop } = useElementBounding(() => gridRef.value?.$el)
const filtersTop = computed(() => pxToRem(headerPLPHeight.value || gridTop.value))

const loadMore = () => {
  const lastCard = gridRef.value?.$el.lastElementChild.querySelector('[data-test-id="product-card-add"]')
  emit('load-more')
  lastCard?.focus()
}

const lazyLoadChunk = (entries: IntersectionObserverEntry[]) => entries.some(
  ({ isIntersecting, boundingClientRect }) =>
    isIntersecting // Prevents the re-rendering of the grid from triggering the logic prematurely
    || boundingClientRect.bottom < 0 // Forces any observed refs above the viewport after SSR to be triggered
)

useIntersectionObserver(lazyLoadRefs, (entries) => {
  if (lazyLoadChunk(entries)) productsToRender.value += eagerLoadedCards
})

const selectAndSortBy = (sort: string) => {
  sortState.value = sort
  sortBy()
}

const selectAndFilterBy = ({ code, selected }: { code: string, selected: string[] }) => {
  filters.value[code].selected = selected
  filterBy(code)
}
</script>
